import humps from 'lodash-humps-ts';
import { authAxios } from '@components/Auth';
import { InsurancePolicy, InsurancePolicyList } from '@models/InsurancePolicy';
import { API_V1_BASE } from '@config';
import { snakes } from '@utils/lodash';
import { NumericString, WithParams } from '@utils/typescript';
import { paginate, Pagination, PaginationParams } from '@models/Pagination';
import { Billing } from '@models/Billing';
import { PaymentMethods } from '@models/PaymentMethod';
import { PolicyTypes } from '@models/policyType';
import { InsuranceTypesEnum } from '@models/InsuranceType';
import { OrderStatuses } from '@models/Order';
import { omit } from 'lodash';

export type GetAllInsurancePolicyParams = PaginationParams & WithParams<{
  insuranceType?: InsuranceTypesEnum | '';
  policyType?: PolicyTypes | '';
  status?: OrderStatuses | '';
  search?: string | '';
}>;

export type CreateInsurancePolicyParams = WithParams<{
  insurer_id: number;
  insurer_team_id: number;
  insurance_template_id: number;
  benefit_template_ids: number[];
  company_id: number;
  insurance_type: string;
  policy_type: string;
  admin_fee_amount: NumericString;
  effective_date: string;
  expiration_date: string;
  description_html: string;
}>;

export type GetInsurancePolicyParams = { id: number };

export type UpdateInsurancePolicyParams = GetInsurancePolicyParams & CreateInsurancePolicyParams;

export type LockInsurancePolicyParams = GetInsurancePolicyParams;

export type PayInsurancePolicyParams = GetInsurancePolicyParams & WithParams<{
  payment_method: PaymentMethods;
  agree_to_terms_and_conditions: boolean;
}>;

export type PayByCashInsurancePolicyParams = GetInsurancePolicyParams;

export type BillingInsurancePolicyParams = GetInsurancePolicyParams;

export class InsurancePolicyApi {
  static getAll = async (params: GetAllInsurancePolicyParams) => {
    return await authAxios
      .get(`${API_V1_BASE}/user/insurance_policies`, { params: snakes(params) })
      .then((response) => paginate(response) as [InsurancePolicyList[], Pagination]);
  };

  static create = async (params: CreateInsurancePolicyParams) => {
    return await authAxios
      .postForm(`${API_V1_BASE}/user/insurance_policies`, {
        ...omit(snakes(params), ['benefit_template_ids']),
        'benefit_template_ids[]': params.benefitTemplateIds,
      })
      .then((response) => humps(response.data) as InsurancePolicy);
  };

  static get = async ({ id }: GetInsurancePolicyParams) => {
    return await authAxios
      .get(`${API_V1_BASE}/user/insurance_policies/${id}`)
      .then((response) => humps(response.data) as InsurancePolicy);
  };

  static update = async ({ id, ...params }: UpdateInsurancePolicyParams) => {
    return await authAxios
      .putForm(`${API_V1_BASE}/user/insurance_policies/${id}`, {
        ...omit(snakes(params), ['benefit_template_ids']),
        'benefit_template_ids[]': params.benefitTemplateIds,
      })
      .then((response) => humps(response.data) as InsurancePolicy);
  };

  static lock = async ({ id }: LockInsurancePolicyParams) => {
    return await authAxios
      .put(`${API_V1_BASE}/user/insurance_policies/${id}/lock`)
      .then((response) => humps(response.data) as InsurancePolicy);
  };

  static pay = async ({ id }: PayInsurancePolicyParams) => {
    return await authAxios
      .post(`${API_V1_BASE}/user/insurance_policies/${id}/pay`)
      .then((response) => humps(response.data) as InsurancePolicy);
  };

  static payByCash = async ({ id }: PayByCashInsurancePolicyParams) => {
    return await authAxios
      .post(`${API_V1_BASE}/user/insurance_policies/${id}/pay_by_cash`)
      .then((response) => humps(response.data) as InsurancePolicy);
  };

  static billing = async ({ id }: BillingInsurancePolicyParams) => {
    return await authAxios
      .get(`${API_V1_BASE}/user/insurance_policies/${id}/billing`)
      .then((response) => humps(response.data) as Billing);
  };
}
